import { Card, Page } from '@ui/themed';
import { t } from 'i18next';
import { useState } from 'react';
import { SparkSuspense } from '../../../components/spark-suspense';
import { useHandlers, withPageProviders } from '../../../context/page-context';
import { useIsMobile } from '../../../hooks/use-media-query';
import { cn } from '../../../lib/styles';
import { SectionList } from '../components/section-list';
import { SectionTree } from '../components/section-tree';
import { useSections } from '../hooks/use-sections';

export const SectionsPage = withPageProviders(SectionsPageContent);

function SectionsPageContent() {
  const { navigationHandler } = useHandlers();
  const { id: companyId } = navigationHandler.params();

  const { data, isLoading, isError, refetch } = useSections(companyId, undefined);

  const [panelDisplay, setPanelDisplay] = useState<'both' | 'list' | 'tree'>('both');

  const sections = data ?? [];

  const isMobile = useIsMobile();

  return (
    <Page
      title={t`features:sections.pages.sections_page.title`}
      description={t`features:sections.pages.sections_page.description`}
      backUrl="/customers/companies"
    >
      <SparkSuspense
        onReload={refetch}
        isError={isError}
        isLoading={isLoading}
        errorMessage={t`features:sections.pages.sections_page.load_error`}
      >
        <table
          className={cn(
            'table-fixed sm:border-separate sm:border-spacing-2',
            panelDisplay === 'both' ? 'w-[200%] sm:w-full' : 'w-[200%]',
          )}
        >
          <colgroup>
            <col className={cn(panelDisplay === 'tree' && 'collapse')} />
            <col
              className={cn(
                panelDisplay === 'list' && 'collapse',
                panelDisplay === 'both' && 'collapse sm:visible',
              )}
            />
          </colgroup>
          <tbody>
            <tr>
              <td className="h-0">
                <div className="h-full">
                  <Card variant="secondary">
                    <Card.Header
                      headingLevel="h1"
                      primaryAction={{
                        icon: isMobile
                          ? 'refresh'
                          : panelDisplay === 'list'
                            ? 'panelCollapse'
                            : 'panelExpand',
                        onClick: () =>
                          setPanelDisplay((v) =>
                            v !== 'list' ? 'list' : isMobile ? 'tree' : 'both',
                          ),
                      }}
                    >
                      {t('features:sections.pages.sections_page.headings.list')}
                    </Card.Header>
                    <Card.Body>
                      <SectionList sections={sections} companyId={companyId || ''} />
                    </Card.Body>
                  </Card>
                </div>
              </td>
              <td className="h-0">
                <div className="h-full">
                  <Card fullHeight>
                    <Card.Header
                      headingLevel="h1"
                      primaryAction={{
                        icon: isMobile
                          ? 'refresh'
                          : panelDisplay === 'tree'
                            ? 'panelCollapse'
                            : 'panelExpand',
                        onClick: () =>
                          setPanelDisplay((v) =>
                            v !== 'tree' ? 'tree' : isMobile ? 'list' : 'both',
                          ),
                      }}
                    >
                      {t('features:sections.pages.sections_page.headings.tree')}
                    </Card.Header>
                    <Card.Body>
                      <SectionTree sections={sections} companyId={companyId ?? ''} />
                    </Card.Body>
                  </Card>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </SparkSuspense>
    </Page>
  );
}
